import { Avatar, AvatarProps, styled } from "@mui/material";

interface AvatarWithTextProps extends AvatarProps {
  text?: string;
  color?: string;
}

const getInitials = (value?: string) => {
  const trimmed = (value ?? "").trim();

  if (!trimmed) {
    return "?";
  }

  if (trimmed.length <= 2) {
    return trimmed.toUpperCase();
  }

  const words = trimmed.split(/\s+/).filter(Boolean);

  if (words.length === 2) {
    return words.map((word) => word[0]?.toUpperCase() ?? "").join("");
  }

  return words[0]?.[0]?.toUpperCase() ?? trimmed[0]?.toUpperCase() ?? "?";
};

const createAvatar = (
  BaseAvatar: typeof Avatar,
  overrides: Record<string, unknown>,
) =>
  styled(({ text, color, children, ...props }: AvatarWithTextProps) => {
    const resolvedChildren = text !== undefined ? getInitials(text) : children;

    return (
      <BaseAvatar
        {...props}
        sx={{
          ...(props.sx ?? {}),
          ...(color ? { backgroundColor: color } : {}),
        }}
      >
        {resolvedChildren}
      </BaseAvatar>
    );
  })<AvatarWithTextProps>(({ theme }) => ({
    flexShrink: 0,
    userSelect: "none",
    ...overrides,
    ...(theme.palette ? {} : {}),
  }));

export const UserAvatar = createAvatar(Avatar, {
  backgroundColor: "#055480",
  color: "#ffffff !important",
  width: 42,
  height: 42,
  fontSize: "1.1rem",
  borderRadius: "50%",
  variant: "circular",
});

export const TeamAvatar = createAvatar(Avatar, {
  backgroundColor: "secondary.main",
  color: "secondary.contrastText",
  width: 42,
  height: 42,
  fontSize: "0.875rem",
  variant: "rounded",
});

export const ClientAvatar = createAvatar(Avatar, {
  backgroundColor: "#055480",
  color: "primary.contrastText",
  width: 32,
  height: 32,
  fontSize: "0.85rem",
  fontWeight: 700,
  borderRadius: "8px",
  variant: "rounded",
});

export default {
  UserAvatar,
  TeamAvatar,
  ClientAvatar,
};
